import React from 'react';
import LegalLayout from './LegalLayout';

export default function TermsAndConditions() {
    return (
        <LegalLayout title="Terms & Conditions" lastUpdated="April 3, 2026">
            <p>
                These Terms and Conditions ("Terms") govern your access to and use of NurtureCRM.ai (the "Service"), operated by AiCoaches dot com LLC ("Company," "we," "us," or "our") under the brand Multiplai.tech. By accessing or using the Service, you agree to be bound by these Terms.
            </p>

            <h2>1. Acceptance of Terms</h2>
            <p>By creating an account, accessing, or using the Service, you confirm that you:</p>
            <ul>
                <li>Are at least 16 years of age</li>
                <li>Have the legal authority to enter into these Terms</li>
                <li>If acting on behalf of an organization, have the authority to bind that organization to these Terms</li>
                <li>Agree to comply with all applicable laws and regulations</li>
            </ul>
            <p>If you do not agree to these Terms, you may not use the Service.</p>

            <h2>2. Description of Service</h2>
            <p>NurtureCRM.ai is a cloud-based customer relationship management platform that provides:</p>
            <ul>
                <li>Contact, account, and deal management</li>
                <li>Sales pipeline tracking and reporting</li>
                <li>Email integration and communication tools</li>
                <li>Calendar sync and booking links</li>
                <li>AI-powered sales insights and assistance</li>
                <li>Email sequences and automation</li>
                <li>Deal rooms and proposal management</li>
                <li>Team collaboration and administration tools</li>
            </ul>

            <h2>3. Accounts and Registration</h2>

            <h3>3.1 Account Creation</h3>
            <p>To use the Service, you must create an account with accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</p>

            <h3>3.2 Organization Accounts</h3>
            <p>Organizations may create accounts with multiple users. The organization owner/administrator is responsible for managing user access, roles, and permissions. The organization is responsible for all activities conducted by its users.</p>

            <h3>3.3 Account Security</h3>
            <p>You must promptly notify us at <a href="mailto:support@aicoaches.com">support@aicoaches.com</a> if you suspect unauthorized access to your account. We are not liable for losses arising from unauthorized use of your credentials.</p>

            <h2>4. Subscription Plans and Payment</h2>

            <h3>4.1 Plans</h3>
            <p>The Service is offered under multiple subscription tiers (Free, Starter, Pro, Enterprise) with varying features, limits, and pricing. Current plan details and pricing are available within the Service.</p>

            <h3>4.2 Billing</h3>
            <ul>
                <li>Paid subscriptions are billed in advance on a monthly or annual basis.</li>
                <li>All fees are non-refundable except as expressly stated in these Terms or required by law.</li>
                <li>We may change pricing with 30 days' advance notice. Continued use after the price change constitutes acceptance.</li>
            </ul>

            <h3>4.3 Free Tier</h3>
            <p>The Free tier is provided at no cost with limited features and usage caps. We reserve the right to modify Free tier limitations at any time.</p>

            <h3>4.4 Overages</h3>
            <p>If you exceed the limits of your plan (users, contacts, etc.), certain features may be restricted until you upgrade or reduce usage.</p>

            <h2>5. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
                <li>Violate any applicable law, regulation, or third-party rights</li>
                <li>Send unsolicited commercial messages (spam) or violate anti-spam laws (CAN-SPAM, GDPR, etc.)</li>
                <li>Upload or transmit malware, viruses, or harmful code</li>
                <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Use automated tools to scrape, crawl, or extract data from the Service without authorization</li>
                <li>Impersonate another person or entity</li>
                <li>Interfere with or disrupt the Service or its infrastructure</li>
                <li>Store or transmit data that infringes intellectual property rights</li>
                <li>Use the Service for any illegal, fraudulent, or deceptive purpose</li>
            </ul>
            <p>We reserve the right to suspend or terminate accounts that violate these usage policies.</p>

            <h2>6. Your Data</h2>

            <h3>6.1 Ownership</h3>
            <p>You retain all rights to your data. We do not claim ownership of any content you upload, create, or store in the Service.</p>

            <h3>6.2 License</h3>
            <p>You grant us a limited, worldwide, non-exclusive license to use, process, and store your data solely for the purpose of providing and improving the Service. This license terminates when you delete your data or close your account.</p>

            <h3>6.3 Data Handling</h3>
            <p>Our handling of your data is governed by our <a href="/privacy">Privacy Policy</a>, <a href="/data-policy">Data Policy</a>, and <a href="/ai-policy">AI Policy</a>.</p>

            <h3>6.4 Responsibility</h3>
            <p>You are responsible for the accuracy, legality, and appropriateness of the data you enter into the Service, including ensuring you have the right to store and process contact information and communications.</p>

            <h2>7. Intellectual Property</h2>

            <h3>7.1 Our Property</h3>
            <p>The Service, including its design, code, features, documentation, and branding (NurtureCRM, Multiplai.tech) are owned by AiCoaches dot com LLC and protected by intellectual property laws. Nothing in these Terms grants you rights to our intellectual property except the limited right to use the Service.</p>

            <h3>7.2 Feedback</h3>
            <p>If you provide suggestions, feature requests, or other feedback about the Service, we may use it without obligation or compensation to you.</p>

            <h2>8. Third-Party Integrations</h2>
            <p>The Service integrates with third-party services (Google Gmail, Google Calendar, etc.). Your use of these integrations is subject to the respective third party's terms of service and privacy policies. We are not responsible for the availability, accuracy, or practices of third-party services.</p>

            <h2>9. AI Features</h2>
            <p>The Service includes AI-powered features as described in our <a href="/ai-policy">AI Policy</a>. AI features are provided "as is" and may produce inaccurate results. You are responsible for reviewing and validating AI-generated content before use.</p>

            <h2>10. Service Availability</h2>

            <h3>10.1 Uptime</h3>
            <p>We strive to maintain high availability but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or factors beyond our control.</p>

            <h3>10.2 Modifications</h3>
            <p>We may modify, update, or discontinue features of the Service at any time. We will provide reasonable notice for material changes that significantly affect your use.</p>

            <h2>11. Limitation of Liability</h2>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
            <ul>
                <li>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.</li>
                <li>WE DISCLAIM ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</li>
                <li>IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.</li>
                <li>WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES.</li>
            </ul>

            <h2>12. Indemnification</h2>
            <p>You agree to indemnify and hold harmless AiCoaches dot com LLC, its officers, directors, employees, and agents from any claims, damages, or expenses arising from:</p>
            <ul>
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Data you submit to the Service</li>
            </ul>

            <h2>13. Termination</h2>

            <h3>13.1 By You</h3>
            <p>You may cancel your subscription and close your account at any time. Cancellation takes effect at the end of the current billing period.</p>

            <h3>13.2 By Us</h3>
            <p>We may suspend or terminate your account if you:</p>
            <ul>
                <li>Violate these Terms or our acceptable use policies</li>
                <li>Fail to pay subscription fees</li>
                <li>Engage in fraudulent or harmful activity</li>
            </ul>
            <p>We will provide notice and an opportunity to cure where reasonable.</p>

            <h3>13.3 Effect of Termination</h3>
            <p>Upon termination, your right to use the Service ceases. Your data will be retained for 90 days, during which you may request an export. After the retention period, data will be permanently deleted.</p>

            <h2>14. Dispute Resolution</h2>

            <h3>14.1 Governing Law</h3>
            <p>These Terms are governed by the laws of the State of Florida, United States, without regard to conflict of law principles.</p>

            <h3>14.2 Arbitration</h3>
            <p>Any dispute arising from these Terms or the Service shall first be attempted to be resolved through good-faith negotiation. If unresolved after 30 days, disputes shall be settled by binding arbitration in Miami, Florida, under the rules of the American Arbitration Association.</p>

            <h3>14.3 Class Action Waiver</h3>
            <p>You agree to resolve disputes individually and waive the right to participate in class action lawsuits or class-wide arbitration.</p>

            <h2>15. General Provisions</h2>
            <ul>
                <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy, Data Policy, and AI Policy, constitute the entire agreement between you and the Company.</li>
                <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in effect.</li>
                <li><strong>Waiver:</strong> Failure to enforce any provision does not constitute a waiver.</li>
                <li><strong>Assignment:</strong> You may not assign these Terms without our consent. We may assign our rights and obligations freely.</li>
                <li><strong>Force Majeure:</strong> We are not liable for failures caused by events beyond our reasonable control.</li>
            </ul>

            <h2>16. Changes to These Terms</h2>
            <p>We may update these Terms from time to time. We will notify you of material changes via email or through the Service at least 30 days before they take effect. Continued use after changes constitutes acceptance.</p>

            <h2>17. Contact Us</h2>
            <p>For questions about these Terms:</p>
            <ul>
                <li><strong>Email:</strong> <a href="mailto:support@aicoaches.com">support@aicoaches.com</a></li>
                <li><strong>Address:</strong> AiCoaches dot com LLC, 111 NE 1ST ST, 8TH FLOOR 89145, Miami, Florida, USA 33132</li>
            </ul>
        </LegalLayout>
    );
}
